import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabase/supabase';
import { useToast } from '../components/layout/ToastProvider';
import FormModal from '../components/common/FormModal';
import ConfirmModal from '../components/common/ConfirmModal';

const { 
  FiPlay, FiPlus, FiMail, FiUserPlus, FiArrowRight, FiMoreVertical, 
  FiSettings, FiActivity, FiTrash2, FiToggleLeft, FiToggleRight,
  FiCheckCircle, FiAlertCircle, FiTerminal, FiCpu, FiBookOpen, FiZap
} = FiIcons;

const TRIGGERS = [
  { id: 'new_client', label: 'Client Joins Program', icon: FiUserPlus },
  { id: 'course_completed', label: 'Course Finished', icon: FiCheckCircle },
  { id: 'lesson_watched', label: 'Lesson Completed', icon: FiPlay },
  { id: 'payment_success', label: 'Payment Received', icon: FiIcons.FiDollarSign }
];

const ACTIONS = [
  { id: 'send_automated_message', label: 'Send Direct Message', icon: FiZap },
  { id: 'send_email', label: 'Send Automated Email', icon: FiMail },
  { id: 'grant_access', label: 'Grant Course Access', icon: FiBookOpen },
  { id: 'add_crm_tag', label: 'Add CRM Tag', icon: FiIcons.FiTag },
];

export default function CoachFlow() {
  const { addToast } = useToast();
  const [flows, setFlows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [logs, setLogs] = useState([]);
  
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [isLogsOpen, setIsLogsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    fetchFlows();
    fetchTemplates();
  }, []);

  const fetchFlows = async () => {
    try {
      const { data, error } = await supabase
        .from('automations_2024')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setFlows(data || []);
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    const { data } = await supabase.from('message_templates_2024').select('*').eq('is_automated', true);
    setTemplates(data || []);
  };

  const handleSaveFlow = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    const formData = new FormData(e.target);
    
    const flowData = {
      title: formData.get('title'),
      trigger_type: formData.get('trigger_type'),
      action_type: formData.get('action_type'),
      metadata: {
        template_id: formData.get('template_id'),
        subject: formData.get('subject') || ''
      },
      is_active: true
    };

    try {
      if (selectedFlow?.id) {
        await supabase.from('automations_2024').update(flowData).eq('id', selectedFlow.id);
        addToast("Flow updated!");
      } else {
        const { data } = await supabase.from('automations_2024').insert([flowData]).select();
        setFlows([data[0], ...flows]);
        addToast("Automation active!");
      }
      setIsBuilderOpen(false);
      fetchFlows();
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const toggleFlow = async (flow) => {
    try {
      await supabase.from('automations_2024').update({ is_active: !flow.is_active }).eq('id', flow.id);
      setFlows(flows.map(f => f.id === flow.id ? { ...f, is_active: !f.is_active } : f));
      addToast(`Flow ${!flow.is_active ? 'Activated' : 'Paused'}`);
    } catch (error) { addToast(error.message, 'error'); }
  };

  const executeTest = async (flow) => {
    addToast("Testing workflow...", "info");
    try {
      await supabase.from('automation_logs_2024').insert([{
        automation_id: flow.id,
        status: 'success',
        message: `Manual test for: ${flow.action_type}`,
        details: { dry_run: true, template: flow.metadata?.template_id }
      }]);
      await supabase.from('automations_2024').update({ last_run_at: new Date() }).eq('id', flow.id);
      addToast("Test successful!", "success");
      fetchFlows();
    } catch (error) { addToast(error.message, "error"); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div></div>;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-3xl font-black text-white tracking-tight">CoachFlow Automations</h1>
          <p className="text-slate-400 mt-1">Intelligent triggers that grow your business while you sleep.</p>
        </div>
        <button 
          onClick={() => { setSelectedFlow(null); setIsBuilderOpen(true); }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-3 transition-all shadow-2xl shadow-indigo-600/30"
        >
          <SafeIcon icon={FiPlus} className="text-xl" /> Create Workflow
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {flows.map((flow) => (
          <motion.div layout key={flow.id} className={`bg-slate-900 border ${flow.is_active ? 'border-slate-800' : 'border-slate-800/30 opacity-60'} rounded-[32px] p-8 shadow-xl relative overflow-hidden group`}>
            <div className="flex justify-between items-start mb-6">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${flow.is_active ? 'bg-indigo-500/10 text-indigo-400' : 'bg-slate-800 text-slate-500'}`}>
                <SafeIcon icon={ACTIONS.find(a => a.id === flow.action_type)?.icon || FiTerminal} className="text-2xl" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setSelectedFlow(flow); setIsBuilderOpen(true); }} className="p-2.5 text-slate-500 hover:text-white rounded-xl hover:bg-slate-800 transition-all"><SafeIcon icon={FiSettings} /></button>
                <button onClick={() => { setSelectedFlow(flow); setIsDeleteOpen(true); }} className="p-2.5 text-slate-500 hover:text-rose-500 rounded-xl hover:bg-rose-500/10 transition-all"><SafeIcon icon={FiTrash2} /></button>
              </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">{flow.title}</h3>
            
            <div className="space-y-3 mb-8">
              <div className="flex items-center gap-3 bg-slate-800/40 p-3 rounded-2xl border border-slate-800">
                <SafeIcon icon={FiCpu} className="text-indigo-400" />
                <p className="text-xs text-slate-400">Trigger: <span className="text-white font-bold">{TRIGGERS.find(t => t.id === flow.trigger_type)?.label}</span></p>
              </div>
              {flow.metadata?.template_id && (
                <div className="flex items-center gap-3 bg-slate-800/40 p-3 rounded-2xl border border-slate-800">
                  <SafeIcon icon={FiZap} className="text-amber-400" />
                  <p className="text-xs text-slate-400">Template: <span className="text-white font-bold">{templates.find(t => t.id === flow.metadata.template_id)?.title || 'Custom'}</span></p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-6 border-t border-slate-800/50">
              <button 
                onClick={() => toggleFlow(flow)}
                className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest ${flow.is_active ? 'text-emerald-400' : 'text-slate-500'}`}
              >
                <SafeIcon icon={flow.is_active ? FiToggleRight : FiToggleLeft} className="text-xl" /> {flow.is_active ? 'Active' : 'Paused'}
              </button>
              <button onClick={() => executeTest(flow)} className="text-indigo-400 hover:text-white text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                <SafeIcon icon={FiPlay} /> Run Test
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      <FormModal 
        isOpen={isBuilderOpen} onClose={() => setIsBuilderOpen(false)} 
        title={selectedFlow ? "Edit Workflow" : "CoachFlow Builder"} 
        onSubmit={handleSaveFlow} loading={modalLoading}
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Workflow Title</label>
            <input name="title" defaultValue={selectedFlow?.title} required className="w-full bg-slate-800 border-none rounded-2xl px-5 py-4 text-white outline-none focus:ring-2 focus:ring-indigo-500" placeholder="e.g. New Student Welcome" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">When this happens...</label>
              <select name="trigger_type" defaultValue={selectedFlow?.trigger_type} className="w-full bg-slate-800 border-none rounded-2xl px-5 py-4 text-white outline-none appearance-none">
                {TRIGGERS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Then do this...</label>
              <select name="action_type" defaultValue={selectedFlow?.action_type} className="w-full bg-slate-800 border-none rounded-2xl px-5 py-4 text-white outline-none appearance-none">
                {ACTIONS.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
            </div>
          </div>

          <div className="p-6 bg-indigo-600/5 rounded-[24px] border border-indigo-500/10 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <SafeIcon icon={FiSettings} className="text-indigo-400" />
              <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">Action Configuration</p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Message Template</label>
                <select name="template_id" defaultValue={selectedFlow?.metadata?.template_id} className="w-full bg-slate-900 border-none rounded-xl px-4 py-3 text-sm text-white outline-none">
                  <option value="">No Template (Manual Content)</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.title} ({t.category})</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">Override Subject / Tag</label>
                <input name="subject" defaultValue={selectedFlow?.metadata?.subject} className="w-full bg-slate-900 border-none rounded-xl px-4 py-3 text-sm text-white outline-none" placeholder="Optional identifier" />
              </div>
            </div>
          </div>
        </div>
      </FormModal>

      <ConfirmModal 
        isOpen={isDeleteOpen} onClose={() => setIsDeleteOpen(false)} 
        onConfirm={async () => {
          await supabase.from('automations_2024').delete().eq('id', selectedFlow.id);
          fetchFlows();
          setIsDeleteOpen(false);
        }} 
        title="Delete Workflow?" 
        message="This will permanently stop this automated flow and delete its logs." 
      />
    </div>
  );
}